import { observable, computed, action, autorun, toJS } from 'mobx';
import { getIn, genGuid } from '../utils';
import * as constants from './const';


function createProfileStructure({ userDetails, addresses, oldStructure }) {
  const profileOrgId = getIn(oldStructure, ['Organization', 'Id']) || genGuid();
  const profileOBPId = getIn(oldStructure, ['Organization', 'OrganizationBusinessPartner', 'Id']) || genGuid();
  const profileBUId = getIn(oldStructure, ['Organization', 'BusinessUnit', 'Id']) || genGuid();
  const profileUBPId = getIn(oldStructure, ['Organization', 'OrganizationBusinessPartner', 'refUserBusinessPartnerId']) || genGuid();
  const addrIds = [
    getIn(oldStructure, ['Organization', 'BusinessUnit', 'BusinessUnitAddress', 0, 'Id']) || genGuid(),
    getIn(oldStructure, ['Organization', 'BusinessUnit', 'BusinessUnitAddress', 1, 'Id']) || genGuid(),
    getIn(oldStructure, ['Organization', 'BusinessUnit', 'BusinessUnitAddress', 2, 'Id']) || genGuid(),
  ];

  let profile = {
    Organization: {
      Id: profileOrgId,
      BusinessUnit: {
        Id: profileBUId,
        refTimeZoneId: userDetails.timeZone,
        BusinessUnitAddress: addresses.map((addr, idx) => {
          if (addr) {
            return {
              Id: addrIds[idx],
              AddressAddition: addr.addition,
              AddressName: userDetails.organization,
              City: addr.city,
              PostalCode: addr.postalCode,
              Street: addr.street,
              refCountryId: addr.country,
              refAddressTypeId: constants.ADDRESS_TYPE[constants.ADDRESS_TYPE_ORDER[idx]],
            }
          } else {
            return null;
          }
        })
      },
      OrganizationBusinessPartner: {
        Id: profileOBPId,
        ConfirmPassword: userDetails.password2,
        Email: userDetails.email,
        FirstName: userDetails.firstName,
        Mobile: userDetails.mobilePhone,
        Fax: userDetails.faxPhone,
        Name: userDetails.lastName,
        Password: userDetails.password1,
        Phone: userDetails.phone,
        Username: userDetails.userName,
        refBusinessUnitId: profileBUId,
        refLanguageId: userDetails.language,
        refOrganizationId: profileOrgId,
        refSalutationId: userDetails.salutation,
        refUserBusinessPartnerId: profileUBPId,
      }
    }
  }
  if (oldStructure) {
    for (let i = 0; i < 3; i++) {
      oldStructure.HomeBusinessUnit[0].BusinessUnitAddress[i] = {
        ...(oldStructure.HomeBusinessUnit[0].BusinessUnitAddress[i] || {}),
        ...(profile.Organization.BusinessUnit.BusinessUnitAddress[i] || {}),
      }
    }

    if (profile.Organization.BusinessUnit.refTimeZoneId) {
      oldStructure.HomeBusinessUnit[0].refTimeZoneId = profile.Organization.BusinessUnit.refTimeZoneId;
    }

    let { Email, Fax, FirstName, Mobile, Name, Phone, refLanguageId, refSalutationId } =
      profile.Organization.OrganizationBusinessPartner;
    oldStructure.OrganizationBusinessPartner[0] = {
      ...oldStructure.OrganizationBusinessPartner[0],
      ...{ Email, Fax, FirstName, Mobile, Name, Phone, refLanguageId, refSalutationId },
    }
    if(oldStructure.CustomerOrganizationBusinessPartner && oldStructure.CustomerOrganizationBusinessPartner.length) {
      oldStructure.CustomerOrganizationBusinessPartner[0].CustomerOrganizationBusinessPartnerAddress = addresses.map((addr, idx) => {
        if (addr) {
          return {
            Id: addrIds[idx],
            AddressAddition: addr.addition,
            AddressName: userDetails.organization,
            City: addr.city,
            PostalCode: addr.postalCode,
            Street: addr.street,
            refCountryId: addr.country,
            refAddressTypeId: constants.ADDRESS_TYPE[constants.ADDRESS_TYPE_ORDER[idx]],
          }
        } else {
          return null;
        }
      })
    }
    return oldStructure;
  } else {
    return profile;
  }
}

function createPasswordChanger({ oldStructure, oldPassword, password1, password2 }) {
  oldStructure.OrganizationBusinessPartner[0].NewPassword = password1;
  oldStructure.OrganizationBusinessPartner[0].ConfirmPassword = password2;
  oldStructure.OrganizationBusinessPartner[0].Password = oldPassword;
  return oldStructure;
}

class PersonalAddress {
  constructor(content) {
    this.content = content;
  }

  @computed get refAddressTypeId() {
    return this.content.refAddressTypeId;
  }

  set refAddressTypeId(v) {
    this.content.refAddressTypeId = v;
  }

  @computed get street() {
    return this.content.Street;
  }

  set street(v) {
    this.content.Street = v;
  }

  @computed get addition() {
    return this.content.AddressAddition;
  }

  set addition(v) {
    this.content.AddressAddition = v;
  }

  @computed get postalCode() {
    return this.content.PostalCode;
  }

  set postalCode(v) {
    this.content.PostalCode = v;
  }

  @computed get city() {
    return this.content.City;
  }

  set city(v) {
    this.content.City = v;
  }

  @computed get country() {
    return this.content.refCountryId;
  }

  set country(v) {
    this.content.refCountryId = v;
  }
}

export class PersonalData {
  @observable content = {};

  constructor(flasher, api, scene, authentication) {
    this.flasher = flasher;
    this.api = api;
    this.scene = scene;
    this.authentication = authentication;
  }

  @action.bound async fetch({orgId}) {
    try {
      let lesseeId = this.authentication.userId;
      this.content = await this.api.getPersonalData(orgId, lesseeId);
    }
    catch (e) {
      console.error(e);
    }
  }

  @action.bound async update({ organizationId, userDetails, addresses }) {
    let lesseeId = this.authentication.userId;
    return await this.api.updatePersonalData({
      organizationId,
      lesseeId,
      profileStructure: createProfileStructure({ userDetails, addresses, oldStructure: toJS(this.content) }),
    });
  }

  @action.bound async changePassword({ oldPassword, password1, password2 }) {
    return await this.api.changePassword({
      profileStructure: createPasswordChanger({ oldStructure: toJS(this.content), oldPassword, password1, password2 })
    })
  }

  @action.bound async create({ organizationId, userDetails, addresses }) {
    return await this.api.createNewLessee({
      organizationId,
      profileStructure: createProfileStructure({ userDetails, addresses })
    });
  }

  @action.bound async confirm({ token, userId }) {
    return await this.api.confirmUserEmail({
      token, userId
    })
  }

  @computed get personalData() {
    return getIn(this.content, ['OrganizationBusinessPartner', 0], {})
  }

  @computed get salutation() {
    return this.personalData.refSalutationId;
  }

  @computed get firstName() {
    return this.personalData.FirstName;
  }

  @computed get lastName() {
    return this.personalData.Name;
  }

  @computed get email() {
    return this.personalData.Email;
  }

  @computed get phone() {
    return this.personalData.Phone;
  }

  @computed get mobilePhone() {
    return this.personalData.Mobile;
  }

  @computed get faxPhone() {
    return this.personalData.Fax;
  }

  @computed get timeZone() {
    return getIn(this.content, ['HomeBusinessUnit', 0, 'refTimeZoneId'])
  }

  @computed get language() {
    return this.personalData.refLanguageId;
  }

  @computed get organization() {
    return getIn(this.content, ['HomeBusinessUnit', 0, 'BusinessUnitAddress', 0, 'AddressName'])
  }

  @computed get addresses() {
    let result = {};
    getIn(this.content, ['HomeBusinessUnit', 0, 'BusinessUnitAddress'], []).forEach(o => {
      const addr = new PersonalAddress(o);
      result[addr.refAddressTypeId] = addr;
    });
    return result;
  }



}


// WEBPACK FOOTER //
// ./src/model/PersonalData.js